import { DataLakeEnums } from 'dashboard-services/api/dataLake'
import { ReduxService } from 'dashboard-services/services'
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder'

import { filesDatalakeActions, groupDialogDatalakeActions } from 'actions/modules/datalake'

export default (() => {
  const { PRIORITY_METADATA, DATA_START_METADATA, DATA_END_METADATA, FORWARD_CURVE_METADATA } = filesDatalakeActions
  const { MODES, TABS } = groupDialogDatalakeActions
  const RESERVED_FIELD_NAMES = [PRIORITY_METADATA, DATA_START_METADATA, DATA_END_METADATA, FORWARD_CURVE_METADATA]
  const DETAILS_KEYS = ["description", "groupTags", "fields", "isDataTimeFrameEnabled", "shouldBeForwardCurvesGroup", "priority"]
  const SHARE_COMPARED_KEYS = ["access", "dataStart", "dataEnd", "message", "sendMessage"]
  const REQUIRED_MESSAGE = "Field required."
  const ACTION_REQUIRED_MESSAGE = "Select at least one action."
  const ALL_DIRTY = { details: true, config: true, share: true }

  const isEqual = ({ first, second }) => JSON.stringify(first) === JSON.stringify(second)

  const hasField = ({ fields = [], name }) => fields.some(field => field.name === name)

  const hasDataTimeFrame = ({ fields = [] }) => hasField({ fields, name: DATA_START_METADATA }) && hasField({ fields, name: DATA_END_METADATA })

  const isForwardCurvesGroup = ({ fields = [] }) => hasField({ fields, name: FORWARD_CURVE_METADATA })

  const getDefaultPriority = ({ fields = [] }) => (fields || []).find(field => field.name === PRIORITY_METADATA)?.defaultValue || DataLakeEnums.GROUP_PRIORITY_VALUE.MEDIUM

  const getFieldsToDisplay = ({ fields = [] }) => fields.filter(field => !RESERVED_FIELD_NAMES.includes(field.name))

  const getFullFieldIndex = ({ fields = [], displayIndex }) => {
    const displayedIndexes = fields
      .map((field, index) => RESERVED_FIELD_NAMES.includes(field.name) ? -1 : index)
      .filter(index => index > -1)
    return displayedIndexes[displayIndex] ?? -1
  }

  const withPriority = ({ fields, priority }) => {
    const fieldsWithoutPriority = fields.filter(field => field.name !== PRIORITY_METADATA)
    return priority
      ? fieldsWithoutPriority.concat({ name: PRIORITY_METADATA, type: DataLakeEnums.GROUP_FIELD_TYPE.NUMBER, defaultValue: priority })
      : fieldsWithoutPriority
  }

  const withDataTimeFrame = ({ fields, initialFields, isDataTimeFrameEnabled }) => {
    if(isDataTimeFrameEnabled) {
      return [DATA_START_METADATA, DATA_END_METADATA]
        .filter(name => !hasField({ fields, name }))
        .reduce((prev, name) => prev.concat({ name, type: DataLakeEnums.GROUP_FIELD_TYPE.DATE_MILLISECONDS, defaultValue: "" }), fields)
    }
    if(hasDataTimeFrame({ fields: initialFields })) {
      return fields.filter(field => field.name !== DATA_START_METADATA && field.name !== DATA_END_METADATA)
    }
    return fields
  }

  const getFieldsToSubmit = ({ fields = [], initialFields = [], priority, isDataTimeFrameEnabled }) =>
    withDataTimeFrame({ fields: withPriority({ fields, priority }), initialFields, isDataTimeFrameEnabled })
      .map(field => field.type === DataLakeEnums.GROUP_FIELD_TYPE.ARRAY ? ReduxService.updateObject(field, { defaultValue: JSON.stringify(field.defaultValue) }) : field)

  const hasApprovalAction = ({ actions = {} }) => Object.values(actions).some(Boolean)

  const getApprovalErrors = ({ approval }) => approval?.isApprovalEnabled
    ? [
      (approval.fileTypes || []).length === 0 ? { tab: TABS.ADVANCED, field: "approvalFileTypes", message: REQUIRED_MESSAGE } : undefined,
      !hasApprovalAction({ actions: approval.actions }) ? { tab: TABS.ADVANCED, field: "approvalActions", message: ACTION_REQUIRED_MESSAGE } : undefined,
      (approval.reviewers || []).length === 0 ? { tab: TABS.ADVANCED, field: "approvalReviewers", message: REQUIRED_MESSAGE } : undefined
    ]
    : []

  const getValidationErrors = ({ mode, groupName, priority, groupConfig, dynamicValidation, dirty = ALL_DIRTY }) => [
    mode === MODES.CREATE && !String(groupName || "").trim() ? { tab: TABS.DETAILS, field: "name", message: REQUIRED_MESSAGE } : undefined,
    dirty.details && !priority ? { tab: TABS.ADVANCED, field: "priority", message: REQUIRED_MESSAGE } : undefined,
    dirty.config && groupConfig?.isValidationEnabled && groupConfig?.isDynamicValidationEnabled && !dynamicValidation?.taskInput?.taskName
      ? { tab: TABS.ADVANCED, field: "taskName", message: REQUIRED_MESSAGE }
      : undefined,
    ...(dirty.config ? getApprovalErrors({ approval: groupConfig?.approval }) : [])
  ].filter(Boolean)

  const pick = ({ object = {}, keys }) => Object.fromEntries(keys.map(key => [key, object[key]]))

  const getDirtyTabs = ({ mode, initialDetails, details, initialConfig, config, shareChanges }) => ({
    details: mode === MODES.CREATE || !isEqual({ first: pick({ object: initialDetails, keys: DETAILS_KEYS }), second: pick({ object: details, keys: DETAILS_KEYS }) }),
    config: !isEqual({ first: initialConfig?.groupConfig, second: config?.groupConfig })
      || !isEqual({ first: initialConfig?.dynamicValidation, second: config?.dynamicValidation })
      || config?.shouldRemoveInvalidParams === true,
    share: (shareChanges?.addedOrChanged?.length || 0) > 0 || (shareChanges?.removed?.length || 0) > 0
  })

  const withGroupName = ({ items, groupName }) => items.map(item => ReduxService.updateObject(item, { groupName }))

  const isSameShare = ({ first, second }) => first.email === second.email && SHARE_COMPARED_KEYS.every(key => first[key] === second[key])

  const getShareChanges = ({ defaultSharedWith = [], sharedWith = [], groupName }) => ({
    addedOrChanged: withGroupName({
      items: sharedWith
        .filter(share => !defaultSharedWith.some(defaultShare => isSameShare({ first: defaultShare, second: share })))
        .map(share => ReduxService.updateObject(share, { url: DashboardLinkFactory.dataLake.group({ groupName, query: "" }) })),
      groupName
    }),
    removed: withGroupName({
      items: defaultSharedWith.filter(defaultShare => !sharedWith.some(share => share.email === defaultShare.email)),
      groupName
    })
  })

  return {
    getFieldsToSubmit,
    getFieldsToDisplay,
    getFullFieldIndex,
    getDefaultPriority,
    hasDataTimeFrame,
    isForwardCurvesGroup,
    getValidationErrors,
    getDirtyTabs,
    getShareChanges
  }
})()
